import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Section, Chip, impactTone, effortTone } from "@/growth/components/_ui";
import * as gc from "@/growth/api";

export default function ApprovalsTab({ wsId, onChanged }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = async () => {
    const r = await gc.listRecos(wsId);
    setItems(r.items || []);
  };
  useEffect(() => { load(); }, [wsId]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (id, dec) => {
    setBusy(id);
    try {
      const reason = dec !== "approve" ? (window.prompt("Reason (optional):") || "") : "";
      await gc.decideReco(id, dec, reason);
      toast.success(dec === "approve" ? "Approved." : dec === "reject" ? "Rejected." : "Deferred.");
      await load();
      onChanged?.();
    } catch { toast.error("Decision failed"); }
    finally { setBusy(null); }
  };

  const pending = items.filter((r) => r.status === "pending");
  const decided = items.filter((r) => r.status !== "pending");

  return (
    <div>
      <Section title="Approval Queue" subtitle="Nothing external happens without your approval. Reject with a reason so the AI learns.">
        {pending.length === 0 ? (
          <p className="text-sm text-slate-500">No pending recommendations. Run an audit or approve a creative concept to generate some.</p>
        ) : (
          <div className="space-y-3">
            {pending.map((r) => (
              <Card key={r.id} className="p-5 border border-slate-200 rounded-2xl" data-testid={`gc-reco-${r.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Chip tone="sky">{r.source}</Chip>
                      <Chip tone="indigo">{r.channel}</Chip>
                      <Chip tone={impactTone(r.impact)}>Impact · {r.impact}</Chip>
                      <Chip tone={effortTone(r.effort)}>Effort · {r.effort}</Chip>
                      <Chip tone="slate">Priority · {r.priority_score}</Chip>
                    </div>
                    <div className="font-bold text-slate-900">{r.title}</div>
                    <div className="text-sm text-slate-600 mt-1">{r.summary}</div>
                    <div className="mt-2 text-xs text-slate-500"><strong>Evidence:</strong> {r.evidence}</div>
                    {r.success_metric && <div className="text-xs text-slate-500"><strong>Metric:</strong> {r.success_metric}</div>}
                  </div>
                  <div className="flex flex-col gap-2 min-w-[120px]">
                    <Button size="sm" onClick={() => decide(r.id, "approve")} disabled={busy === r.id} className="bg-emerald-600 hover:bg-emerald-700 rounded-full" data-testid={`gc-reco-approve-${r.id}`}>Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => decide(r.id, "defer")} disabled={busy === r.id} className="rounded-full">Defer</Button>
                    <Button size="sm" variant="outline" onClick={() => decide(r.id, "reject")} disabled={busy === r.id} className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50">Reject</Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Section>

      {decided.length > 0 && (
        <Section title="Decided" subtitle="Immutable log of every decision, with reason and approver.">
          <div className="space-y-2">
            {decided.map((r) => (
              <Card key={r.id} className="p-3 border border-slate-200 rounded-xl">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{r.title}</div>
                    <div className="text-xs text-slate-500">{r.decided_by} · {r.decided_at ? new Date(r.decided_at).toLocaleString() : ""}{r.decision_reason ? ` · ${r.decision_reason}` : ""}</div>
                  </div>
                  <Chip tone={r.status === "approved" ? "emerald" : r.status === "rejected" ? "rose" : "amber"}>{r.status}</Chip>
                </div>
              </Card>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
